import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import rehypeExternalLinks from "rehype-external-links";
import { removeCustomTags } from "../../utils/renderUtility";
import {
  CharactersIconSvg,
  FavoriteIcon,
  ThreeDotSvg,
} from "../cbb-builder/components/AiBotAssets";
import Pagination from "../cbb-builder/Pagination";
import { motion, AnimatePresence } from "framer-motion";
import { axiosPost } from "@/services/axiosService";
import { useQueryClient } from "@tanstack/react-query";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useResponseCache } from "@/hooks/useResponseCache";
import NoDataOnboard from "../agent/NoDataOnboard";
import { useDispatch } from "react-redux";
import { userSlice } from "@/redux/User";
import { useGetCategories } from "@/hooks/useGetCategories";
import AddQuestionModal from "./AddQuestionModal";

const tagsToEliminate = [
  "<links-cbb>",
  "</links-cbb>",
  "<clickable-options-cbb>",
  "</clickable-options-cbb>",
];

type Props = {
  selectedBot: any;
  setPage?: any;
  fromDate?: any;
  toDate?: any;
  selectedFilter?: any;
  searchString?: any;
  page?: any;
  pageSize?: any;
  hidePagination?: boolean;
  showCategories?: boolean;
  setShowCategories?: (value: boolean) => void;
  selectedCategory?: any;
  setSelectedCategory?: (value: any) => void;
  selectedItems?: string[];
  setSelectedItems?: (value: string[]) => void;
  calledFromBuilder?: any;
  appliedCategories?: string[];
  sort?: any;
  setSort?: any;
};

const QuestionSkeleton = () => (
  <motion.li
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 10 }}
    transition={{
      duration: 0.2,
      ease: "easeInOut",
      opacity: { duration: 0.15 },
    }}
    className="border-[rgba(0, 0, 0, 0.10)] h-[200px] rounded-[10px] border bg-[#F8F8F880] p-[16px]"
  >
    <SkeletonTheme baseColor="#D1D1D1" highlightColor="#E5E5E5">
      <div className="border-b-[1px] border-b-[rgba(0,0,0,0.10)]">
        <div className="mb-[6px] flex items-center gap-[6px]">
          <Skeleton width={12} height={15} borderRadius={2} />
          <Skeleton width={60} height={12} borderRadius={2} />
          <Skeleton width={40} height={20} borderRadius={5} />
        </div>
        <div className="relative mb-[16px] flex justify-between">
          <div className="w-[80%]">
            <Skeleton
              count={2}
              height={16}
              borderRadius={4}
              className="mb-[4px]"
            />
          </div>
          <Skeleton width={20} height={20} borderRadius={4} />
        </div>
      </div>
      <div className="mt-[12px]">
        <div className="relative">
          <div className="mb-[6px] flex items-center justify-between">
            <Skeleton width={100} height={12} borderRadius={4} />
            <div className="flex gap-[10px]">
              <Skeleton width={16} height={16} borderRadius={4} />
              <Skeleton width={16} height={16} borderRadius={4} />
            </div>
          </div>
          <div className="mt-[12px] space-y-[8px]">
            <Skeleton count={2} height={16} borderRadius={4} />
          </div>
        </div>
      </div>
    </SkeletonTheme>
  </motion.li>
);

const StarButton = ({
  isStarred,
  onClick,
}: {
  isStarred: boolean;
  onClick: () => void;
}) => (
  <div
    onClick={onClick}
    className="cursor-pointer transition-transform hover:scale-110"
  >
    <FavoriteIcon isFilled={isStarred} />
  </div>
);

function MostAskedQuestions({
  selectedBot,
  fromDate,
  toDate,
  selectedFilter,
  searchString,
  page,
  pageSize,
  setPage,
  hidePagination,
  showCategories,
  setShowCategories,
  selectedCategory,
  setSelectedCategory,
  selectedItems,
  setSelectedItems,
  calledFromBuilder,
  appliedCategories,
  sort,
  setSort,
}: Props) {
  const [categoryPage, setCategoryPage] = useState<number>(1);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(
    window.innerWidth < 1280,
  );
  const { categories, categoryPagination }: any = useGetCategories({
    selectedBot,
    selectedFilter: selectedFilter as any,
    page: categoryPage,
    pageSize: 10,
    hidePagination,
    searchString,
  });
  const [expandedAnswers, setExpandedAnswers] = useState<{
    [key: string]: boolean;
  }>({});
  const [openDropdowns, setOpenDropdowns] = useState<{
    [key: string]: boolean;
  }>({});
  const [contentHeights, setContentHeights] = useState<{
    [key: string]: number;
  }>({});
  const contentRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const {
    questions,
    pagination,
    refetch: refetchResponseCache,
    isLoading: improveBotLoading,
  }: any = useResponseCache({
    selectedBot,
    fromDate,
    toDate,
    selectedFilter: selectedFilter as any,
    page,
    pageSize,
    selectedCategory: Array.isArray(selectedCategory)
      ? selectedCategory.join(",")
      : selectedCategory,
    searchString,
    hidePagination,
    calledFromBuilder,
    sort,
  });

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [loaderState, setLoaderState] = useState<
    "delete" | "edit" | "unarchive" | null
  >(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const [preservingId, setPreservingId] = useState<string | null>(null);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [openAddQuestionModal, setOpenAddQuestionModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<any>(null);
  const [hoveredCategoryBadge, setHoveredCategoryBadge] = useState<
    string | null
  >(null);
  const [categoryDropdownPosition, setCategoryDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const categoryBadgeRefs = useRef<{ [key: string]: HTMLDivElement | null }>(
    {},
  );
  const actionButtonRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [actionDropdownPosition, setActionDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const handleCancel = useCallback(() => {
    setOpenAddQuestionModal(false);
    setSelectedQuestion(null);
  }, []);

  const handleCategoryBadgeHover = (cacheResponseId: string) => {
    const badgeElement = categoryBadgeRefs.current[cacheResponseId];
    if (badgeElement) {
      const rect = badgeElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const currentQuestion = questions.find(
        (q: any) => q.cacheResponseId === cacheResponseId,
      );
      const maxCategoriesToShow = isSmallScreen ? 1 : 3;
      const remainingCategories =
        currentQuestion?.categories?.length > maxCategoriesToShow
          ? currentQuestion.categories.length - maxCategoriesToShow
          : 0;
      const estimatedDropdownHeight = Math.min(
        remainingCategories * 35 + 16,
        200,
      );

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldOpenUpwards =
        spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow;

      const position = shouldOpenUpwards
        ? { top: rect.top - estimatedDropdownHeight - 2, left: rect.left }
        : { top: rect.bottom + 2, left: rect.left };

      setCategoryDropdownPosition(position);
      setHoveredCategoryBadge(cacheResponseId);
    }
  };

  const handleCategoryBadgeLeave = () => {
    setHoveredCategoryBadge(null);
    setCategoryDropdownPosition(null);
  };

  const handleSelectAll = (checked: boolean) => {
    if (!setSelectedItems) return;
    if (checked) {
      setSelectedItems(questions.map((q: any) => q.cacheResponseId));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (cacheResponseId: string, checked: boolean) => {
    if (!setSelectedItems || !selectedItems) return;
    if (checked) {
      setSelectedItems([...selectedItems, cacheResponseId]);
    } else {
      setSelectedItems(selectedItems.filter((id) => id !== cacheResponseId));
    }
  };

  const isAllSelected =
    questions &&
    questions.length > 0 &&
    selectedItems &&
    selectedItems.length === questions.length;

  const toggleDropdown = (cacheResponseId: string) => {
    const actionButton = actionButtonRefs.current[cacheResponseId];

    setOpenDropdowns((prev) => {
      if (prev[cacheResponseId]) {
        setActionDropdownPosition(null);
        return {};
      }

      // Calculate position for createPortal
      if (actionButton) {
        const rect = actionButton.getBoundingClientRect();
        setActionDropdownPosition({
          top: rect.bottom + 5,
          left: rect.right - 150, // Position dropdown to the left of the button
        });
      }

      return { [cacheResponseId]: true };
    });
  };

  useEffect(() => {
    if (setShowCategories) {
      setShowCategories(true);
    }
  }, [selectedFilter, setShowCategories]);

  useEffect(() => {
    if (showCategories && setSelectedItems) {
      setSelectedItems([]);
    }
  }, [showCategories, setSelectedItems]);

  useEffect(() => {
    if (setSelectedItems) {
      setSelectedItems([]);
    }
  }, [selectedFilter, setSelectedItems]);

  const toggleAnswer = (cacheResponseId: string) => {
    setExpandedAnswers((prev) => ({
      ...prev,
      [cacheResponseId]: !prev[cacheResponseId],
    }));
  };

  const handleEdit = (query: any) => {
    if (!!loaderState) return;
    setSelectedQuestion(query);
    setOpenAddQuestionModal(true);
    setOpenDropdowns({});
  };

  const handlePreservation = async (
    cacheResponseId: string,
    question: string,
    preserve: boolean,
  ) => {
    if (!!loaderState) return;
    setPreservingId(cacheResponseId);

    try {
      const query = questions.find(
        (q: any) => q.cacheResponseId === cacheResponseId,
      );
      if (!query) return;

      await axiosPost(`/analysis/update`, {
        cacheResponseId,
        question,
        answer: query.answer,
        preserved: preserve,
        fromPreservationMode: true,
      });
      await refetchResponseCache();

      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "success",
          message: preserve
            ? "Question preserved successfully!"
            : "Question unpreserved successfully!",
        }),
      );
    } catch (error) {
      console.error("Error preserving question:", error);

      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message: "Failed to preserve question",
        }),
      );
    }
    setPreservingId(null);
  };

  const handleSave = async (
    cacheResponseId: string,
    question: string,
    answer: string,
  ) => {
    const existingAnswer =
      questions.find((q) => q.cacheResponseId === cacheResponseId)?.answer ||
      "";

    if (!!loaderState || !answer || answer === existingAnswer) return;
    setLoaderState("edit");

    try {
      await axiosPost(`/analysis/update`, {
        cacheResponseId,
        question,
        answer: answer,
        preserved: true,
        fromPreservationMode: true,
      });
      queryClient.invalidateQueries({ queryKey: ["allQuestionsData"] });
      await refetchResponseCache();

      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "success",
          message: "Response saved successfully!",
        }),
      );
    } catch (error) {
      console.error("Error updating answer:", error);

      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message: "Failed to update response. Please try again.",
        }),
      );
    }
    setLoaderState(null);
  };

  const handleUnarchive = async (cacheResponseId) => {
    try {
      setLoaderState("unarchive");
      await axiosPost(`/analysis/unarchieve`, {
        cacheResponseId: [cacheResponseId],
      });
      queryClient.invalidateQueries({ queryKey: ["allQuestionsData"] });
      handleCancel();
    } catch (error) {
      console.error("Error updating answer:", error);
    }
    setTimeout(() => {
      setLoaderState(null);
      setActionDropdownPosition(null);
    }, 1000);
  };

  const handleDelete = async (cacheResponseId) => {
    if (!!loaderState) return;
    setLoaderState("delete");
    const hardDelete = selectedFilter === "expired";

    try {
      await axiosPost(`/analysis/delete`, {
        cacheResponseId: [cacheResponseId],
        hardDelete,
      });
      queryClient.invalidateQueries({ queryKey: ["allQuestionsData"] });
      handleCancel();

      setOpenDropdowns({});
      setActionDropdownPosition(null);
    } catch (error) {
      console.error("Error deleting question:", error);
    }
    setTimeout(() => {
      setLoaderState(null);
    }, 1000);
  };

  useEffect(() => {
    setTimeout(() => {
      if (questions && Array.isArray(questions)) {
        questions.forEach((query) => {
          const element = contentRefs.current[query.cacheResponseId];
          if (element) {
            const height = element.querySelector("p")?.scrollHeight || 0;
            setContentHeights((prev) => ({
              ...prev,
              [query.cacheResponseId]: height,
            }));
          }
        });
      }
    }, 100);
  }, [questions]);

  useEffect(() => {
    if (questions && Array.isArray(questions) && questions.length > 0) {
      const allCollapsed = questions.reduce(
        (acc, question) => {
          acc[question.cacheResponseId] = false;
          return acc;
        },
        {} as { [key: string]: boolean },
      );
      setExpandedAnswers(allCollapsed);
    }
  }, [questions]);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1280);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if clicked inside any action button
      const clickedInsideActionButton = Object.entries(
        actionButtonRefs.current,
      ).some(([id, ref]) => ref?.contains(target));

      // Check if clicked inside the portaled dropdown
      const clickedInsidePortalDropdown =
        target instanceof Element &&
        target.closest(".fixed.z-\\[9999\\].w-\\[150px\\]");

      if (!clickedInsideActionButton && !clickedInsidePortalDropdown) {
        setOpenDropdowns({});
        setActionDropdownPosition(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (improveBotLoading) {
    return (
      <div className="flex h-[calc(100%-155px)] flex-col gap-[20px]">
        <ul className="grid gap-[10px]">
          <AnimatePresence>
            {[1, 2, 3].map((index) => (
              <QuestionSkeleton key={index} />
            ))}
          </AnimatePresence>
        </ul>
      </div>
    );
  }

  return (
    <>
      <div className="h-[calc(100%-60px)]">
        <div className="trained_links_wrapper p-relative new-scrollbar h-unset">
          <div className="new-scrollbar h-full overflow-auto">
            {questions && Array.isArray(questions) && questions.length > 0 ? (
              <table className="links_table w-100 all_trained_links_table improve-agent-table most_asked_ques_table mt-0">
                <thead className="w-100 flex bg-[#f9f9f9]">
                  <tr className="stiky top-0 grid w-full !grid-cols-[50px_1fr_140px_105px_77px]">
                    <th className="flex items-center justify-center">
                      <label className="checkbox-container link-mark mb-0 flex w-[16px] flex-shrink-0 items-center">
                        <input
                          className="bot-check h-[unset]"
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                        <span className="mark">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <rect
                              width="16"
                              height="16"
                              rx="5"
                              fill="#FB5F66"
                            />
                            <path
                              d="M11.6923 4.11088C11.3794 3.90411 10.9616 3.99489 10.7589 4.31353L6.9773 10.2532L5.13304 8.51563C4.85926 8.25774 4.43251 8.2747 4.17942 8.55368C3.92634 8.83243 3.94298 9.26775 4.21677 9.52541L6.63828 11.8068C6.63828 11.8068 6.70779 11.8677 6.73996 11.8891C6.85334 11.9642 6.98067 12 7.10665 12C7.32757 12 7.54421 11.8895 7.67356 11.6864L11.8912 5.06198C12.0941 4.74334 12.005 4.31742 11.6923 4.11088Z"
                              fill="white"
                            />
                          </svg>
                        </span>
                      </label>
                    </th>
                    <th className="flex items-center gap-[10px]">
                      {/* <label className="checkbox-container link-mark no-m !flex">
                          <input
                            className="bot-check"
                            type="checkbox"
                            checked={isAllSelected}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                          />
                          <span className="mark">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <rect width="16" height="16" rx="5" />
                              <path
                                d="M11.6923 4.11088C11.3794 3.90411 10.9616 3.99489 10.7589 4.31353L6.9773 10.2532L5.13304 8.51563C4.85926 8.25774 4.43251 8.2747 4.17942 8.55368C3.92634 8.83243 3.94298 9.26775 4.21677 9.52541L6.63828 11.8068C6.63828 11.8068 6.70779 11.8677 6.73996 11.8891C6.85334 11.9642 6.98067 12 7.10665 12C7.32757 12 7.54421 11.8895 7.67356 11.6864L11.8912 5.06198C12.0941 4.74334 12.005 4.31742 11.6923 4.11088Z"
                                fill="white"
                              />
                            </svg>
                          </span>
                        </label> */}
                      Question
                    </th>
                    <th className="flex items-center justify-center gap-[10px]">
                      Feedback{" "}
                      {/* <CharactersIconSvg
                        sort={sort}
                        setSort={setSort}
                        type="positive"
                      /> */}
                    </th>
                    <th
                      className="flex cursor-pointer items-center justify-center gap-[10px]"
                      onClick={() => {
                        let newSort = `count:-1`;
                        if (sort === "count:-1") {
                          newSort = "count:1";
                        }
                        setSort(newSort);
                      }}
                    >
                      Count{" "}
                      {sort === "count:-1" ? (
                        <DecreasedSortIcon />
                      ) : (
                        <IncreasedSortIcon />
                      )}
                    </th>
                    <th className="flex items-center justify-center">Action</th>
                  </tr>
                </thead>
                <tbody className="flex !h-max flex-col items-center justify-start overflow-auto">
                  <AnimatePresence>
                    {questions.map((query) => (
                      <motion.tr
                        key={query.cacheResponseId}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{
                          duration: 0.2,
                          ease: "easeInOut",
                          opacity: { duration: 0.15 },
                        }}
                        className="grid w-full !grid-cols-[50px_1fr_140px_105px_77px]"
                      >
                        <td className="flex items-start justify-center">
                          <label className="checkbox-container link-mark mb-0 mt-4 flex w-[18px] flex-shrink-0 items-center">
                            <input
                              className="bot-check h-[unset]"
                              type="checkbox"
                              checked={
                                selectedItems?.includes(
                                  query.cacheResponseId,
                                ) || false
                              }
                              onChange={(e) =>
                                handleSelectItem(
                                  query.cacheResponseId,
                                  e.target.checked,
                                )
                              }
                            />
                            <span className="mark">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <rect
                                  width="16"
                                  height="16"
                                  rx="5"
                                  fill="#FB5F66"
                                />
                                <path
                                  d="M11.6923 4.11088C11.3794 3.90411 10.9616 3.99489 10.7589 4.31353L6.9773 10.2532L5.13304 8.51563C4.85926 8.25774 4.43251 8.2747 4.17942 8.55368C3.92634 8.83243 3.94298 9.26775 4.21677 9.52541L6.63828 11.8068C6.63828 11.8068 6.70779 11.8677 6.73996 11.8891C6.85334 11.9642 6.98067 12 7.10665 12C7.32757 12 7.54421 11.8895 7.67356 11.6864L11.8912 5.06198C12.0941 4.74334 12.005 4.31742 11.6923 4.11088Z"
                                  fill="white"
                                />
                              </svg>
                            </span>
                          </label>
                        </td>
                        <td className="flex items-start gap-[15px]">
                          {/* <label className="checkbox-container link-mark no-m !flex h-[18px] w-[18px]">
                              <input
                                className="bot-check"
                                type="checkbox"
                                checked={
                                  selectedItems?.includes(
                                    query.cacheResponseId,
                                  ) || false
                                }
                                onChange={(e) =>
                                  handleSelectItem(
                                    query.cacheResponseId,
                                    e.target.checked,
                                  )
                                }
                              />
                              <span className="mark">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <rect width="16" height="16" rx="5" />
                                  <path
                                    d="M11.6923 4.11088C11.3794 3.90411 10.9616 3.99489 10.7589 4.31353L6.9773 10.2532L5.13304 8.51563C4.85926 8.25774 4.43251 8.2747 4.17942 8.55368C3.92634 8.83243 3.94298 9.26775 4.21677 9.52541L6.63828 11.8068C6.63828 11.8068 6.70779 11.8677 6.73996 11.8891C6.85334 11.9642 6.98067 12 7.10665 12C7.32757 12 7.54421 11.8895 7.67356 11.6864L11.8912 5.06198C12.0941 4.74334 12.005 4.31742 11.6923 4.11088Z"
                                    fill="white"
                                  />
                                </svg>
                              </span>
                            </label> */}

                          <div className="w-full overflow-hidden">
                            <div className="flex items-start gap-2">
                              <p className="flex flex-1 cursor-pointer items-start text-[14px] font-medium text-[#000000B2]">
                                {query.query}
                                <svg
                                  onClick={() => {
                                    handlePreservation(
                                      query.cacheResponseId,
                                      query.query,
                                      !query.preserved,
                                    );
                                  }}
                                  className="ml-2 flex-shrink-0"
                                  width="17"
                                  height="17"
                                  viewBox="0 0 17 17"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M10.9238 5.88867L11.0986 6.24219L11.4893 6.29883L15.959 6.94824L12.7256 10.1016L12.4424 10.377L12.5098 10.7656L13.2734 15.2188L9.27344 13.1162L8.9248 12.9336L8.5752 13.1162L4.5752 15.2188L5.33984 10.7656L5.40625 10.377L5.12402 10.1016L1.88867 6.94824L6.36035 6.29883L6.75 6.24219L6.9248 5.88867L8.92383 1.83594L10.9238 5.88867Z"
                                    stroke={
                                      query.preserved
                                        ? "#FD9D03"
                                        : "currentColor"
                                    }
                                    strokeWidth="1.5"
                                    fill={query.preserved ? "#FD9D03" : "none"}
                                  />
                                </svg>
                              </p>
                            </div>
                            <div className="relative">
                              <motion.div
                                initial={{ height: "70px" }}
                                animate={{
                                  height: expandedAnswers[query.cacheResponseId]
                                    ? "auto"
                                    : "70px",
                                }}
                                transition={{
                                  duration: 0.3,
                                  ease: "easeInOut",
                                }}
                                className="overflow-hidden"
                              >
                                <div className="mt-[10px] flex items-center gap-[2px] text-[12px] font-medium uppercase text-[#666666]">
                                  <svg
                                    width="10"
                                    height="12"
                                    viewBox="0 0 10 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M9.43281 8.33073C9.37448 8.2724 9.37448 8.21406 9.31614 8.15573L6.63281 5.41406C6.39948 5.18073 5.99115 5.23906 5.81615 5.4724C5.64115 5.70573 5.64115 5.9974 5.81615 6.23073L7.50781 7.9224H3.42448C2.43281 7.9224 1.67448 7.16406 1.67448 6.1724V0.747396C1.67448 0.397396 1.44115 0.164062 1.09115 0.164062C0.741146 0.164062 0.507812 0.397396 0.507812 0.747396V6.23073C0.507812 7.86406 1.79115 9.1474 3.42448 9.1474H7.50781L5.81615 10.8391C5.58281 11.0724 5.58281 11.4224 5.81615 11.6557C5.93281 11.7724 6.04948 11.8307 6.22448 11.8307C6.39948 11.8307 6.51615 11.7724 6.63281 11.6557L9.31614 8.9724C9.37448 8.91406 9.43281 8.85573 9.43281 8.7974C9.49115 8.6224 9.49115 8.4474 9.43281 8.33073Z"
                                      fill="#666666"
                                    />
                                  </svg>
                                  Bot Answer
                                </div>
                                <div className="flex items-start justify-between">
                                  <p
                                    className={`mt-[8px] w-full break-words break-all text-[12px] leading-[18px] text-[#666666] ${!expandedAnswers[query.cacheResponseId] && query.answer.length > 100 ? "line-clamp-2" : ""}`}
                                  >
                                    <Markdown
                                      remarkPlugins={[remarkGfm]}
                                      rehypePlugins={[
                                        [
                                          rehypeExternalLinks,
                                          {
                                            target: "_blank",
                                            properties: {
                                              class: "response-link",
                                            },
                                          },
                                        ],
                                      ]}
                                      components={{
                                        li(props) {
                                          const {
                                            children,
                                            className,
                                            node,
                                            ...rest
                                          } = props;
                                          const match = /task-list-item/.exec(
                                            className || "",
                                          );

                                          return match ? null : (
                                            <li {...rest} className={className}>
                                              {children}
                                            </li>
                                          );
                                        },
                                        p(props) {
                                          const { children, ...rest } = props;
                                          return <p {...rest}>{children}</p>;
                                        },
                                      }}
                                    >
                                      {removeCustomTags(
                                        query.answer,
                                        tagsToEliminate,
                                      )}
                                    </Markdown>
                                  </p>
                                </div>
                              </motion.div>

                              {query.answer.length > 100 && (
                                <div className="mt-2">
                                  <span
                                    onClick={() =>
                                      toggleAnswer(query.cacheResponseId)
                                    }
                                    className="hover:text-primary-clr/80 cursor-pointer text-[12px] text-primary-clr transition-colors duration-200"
                                  >
                                    {expandedAnswers[query.cacheResponseId]
                                      ? "See less"
                                      : "See more"}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="mt-[10px] overflow-auto">
                              <div className="categories">
                                {(() => {
                                  if (
                                    query.categories &&
                                    Array.isArray(query.categories) &&
                                    query.categories.length > 0
                                  ) {
                                    const maxCategoriesToShow = isSmallScreen
                                      ? 1
                                      : 3;
                                    return (
                                      <div className="flex flex-row gap-[8px]">
                                        {query.categories
                                          .slice(0, maxCategoriesToShow)
                                          .map((categoryName, index) => {
                                            return (
                                              <span
                                                key={index}
                                                className="flex w-auto items-center gap-[5px] whitespace-nowrap rounded-[4px] px-[8px] py-[2px] text-[11px]"
                                                style={{
                                                  backgroundColor: "#6666661A",
                                                  color: "#444444",
                                                }}
                                              >
                                                <svg
                                                  width="16"
                                                  height="16"
                                                  viewBox="0 0 16 16"
                                                  fill="none"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                >
                                                  <g clipPath="url(#clip0_8676_39071)">
                                                    <path
                                                      d="M8.46559 7.53763H12.1989C12.9708 7.53763 13.5989 6.9095 13.5989 6.13763V4.73763C13.5989 3.96577 12.9708 3.33763 12.1989 3.33763H10.9921L10.3323 2.67777C10.2458 2.5908 10.143 2.52185 10.0297 2.47491C9.91646 2.42797 9.795 2.40397 9.67239 2.4043H8.46559C7.69373 2.4043 7.06559 3.03243 7.06559 3.8043V4.73763H3.33226V2.87097C3.33226 2.7472 3.28309 2.6285 3.19558 2.54098C3.10806 2.45347 2.98936 2.4043 2.86559 2.4043C2.74182 2.4043 2.62313 2.45347 2.53561 2.54098C2.44809 2.6285 2.39893 2.7472 2.39893 2.87097V9.4043C2.39893 10.6909 3.44566 11.7376 4.73226 11.7376H7.06559V12.2043C7.06559 12.9762 7.69373 13.6043 8.46559 13.6043H12.1989C12.9708 13.6043 13.5989 12.9762 13.5989 12.2043V10.8043C13.5989 10.0324 12.9708 9.4043 12.1989 9.4043H10.9921L10.3323 8.74443C10.2458 8.65747 10.143 8.58852 10.0297 8.54158C9.91646 8.49464 9.795 8.47063 9.67239 8.47097H8.46559C7.69373 8.47097 7.06559 9.0991 7.06559 9.87097V10.8043H4.73226C3.96039 10.8043 3.33226 10.1762 3.33226 9.4043V5.67097H7.06559V6.13763C7.06559 6.9095 7.69373 7.53763 8.46559 7.53763ZM7.99893 9.87097C7.99893 9.61337 8.20846 9.4043 8.46559 9.4043H9.67239L10.3323 10.0642C10.5087 10.2406 10.7429 10.3376 10.9921 10.3376H12.1989C12.4561 10.3376 12.6656 10.5467 12.6656 10.8043V12.2043C12.6656 12.4619 12.4561 12.671 12.1989 12.671H8.46559C8.20846 12.671 7.99893 12.4619 7.99893 12.2043V9.87097ZM7.99893 3.8043C7.99893 3.5467 8.20846 3.33763 8.46559 3.33763H9.67239L10.3323 3.9975C10.5087 4.1739 10.7429 4.27097 10.9921 4.27097H12.1989C12.4561 4.27097 12.6656 4.48003 12.6656 4.73763V6.13763C12.6656 6.39523 12.4561 6.6043 12.1989 6.6043H8.46559C8.20846 6.6043 7.99893 6.39523 7.99893 6.13763V3.8043Z"
                                                      fill="#444444"
                                                    />
                                                  </g>
                                                  <defs>
                                                    <clipPath id="clip0_8676_39071">
                                                      <rect
                                                        width="11.2"
                                                        height="11.2"
                                                        fill="white"
                                                        transform="translate(2.3999 2.40039)"
                                                      />
                                                    </clipPath>
                                                  </defs>
                                                </svg>
                                                {categoryName}
                                              </span>
                                            );
                                          })}
                                        {query.categories.length >
                                          maxCategoriesToShow && (
                                          <div
                                            ref={(el) =>
                                              (categoryBadgeRefs.current[
                                                query.cacheResponseId
                                              ] = el)
                                            }
                                            className="relative cursor-pointer"
                                            onMouseEnter={() =>
                                              handleCategoryBadgeHover(
                                                query.cacheResponseId,
                                              )
                                            }
                                            onMouseLeave={
                                              handleCategoryBadgeLeave
                                            }
                                          >
                                            <span className="flex w-auto cursor-pointer items-center whitespace-nowrap rounded-[5px] bg-[#F5F5F5] px-[8px] py-[2px] text-[11px] font-medium text-[#666666]">
                                              +
                                              {query.categories.length -
                                                maxCategoriesToShow}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  } else if (
                                    query.categories &&
                                    typeof query.categories === "string"
                                  ) {
                                    return (
                                      <span className="inline-flex items-center rounded-[5px] bg-[#FB5F6610] px-[8px] py-[2px] text-[11px] font-medium text-[#FB5F66]">
                                        {query.categories}
                                      </span>
                                    );
                                  } else {
                                    return "";
                                  }
                                })()}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="flex items-center justify-center gap-[6px] whitespace-nowrap">
                          <span className="flex items-center gap-[5px] text-[#666666]">
                            <img
                              src={
                                "https://dlvkyia8i4zmz.cloudfront.net/iaNKyDgQIWX8kEkjZ8GP___.png"
                              }
                              alt="positive"
                            />
                            {query.positive}
                          </span>
                          <hr className="h-[16px] w-[1px] bg-[#666666]" />
                          <span className="flex items-center gap-[5px] text-[#666666]">
                            <img
                              src={
                                "https://dlvkyia8i4zmz.cloudfront.net/cs8BwHeSQsOynNwQazvZ___.png"
                              }
                              alt="positive"
                            />
                            {query.negative}
                          </span>
                        </td>
                        <td className="flex items-center justify-center gap-[6px] whitespace-nowrap">
                          <div className="flex h-[20px] items-center justify-center rounded-[5px] px-[6px] text-[14px] font-normal text-[#000000CC]">
                            {query.count}
                          </div>
                        </td>
                        <td className="relative flex items-center justify-center gap-[10px]">
                          <div
                            ref={(el) =>
                              (actionButtonRefs.current[query.cacheResponseId] =
                                el)
                            }
                            onClick={() =>
                              toggleDropdown(query.cacheResponseId)
                            }
                            className="cursor-pointer"
                          >
                            <ThreeDotSvg />
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-[10px] rounded-[10px] p-[20px] text-center">
                <NoDataOnboard />
                <h3 className="text-[16px] font-medium text-text-clr">
                  No Data Available
                </h3>
                <p className="text-[14px] text-[#666666]">
                  No questions found for the selected criteria. Try adjusting
                  your filters or date range.
                </p>
              </div>
            )}
          </div>
          {pagination &&
          pagination.totalPages &&
          pagination.totalPages > 0 &&
          !hidePagination ? (
            <div className="bg-[#f9f9f9] p-[20px]">
              <Pagination
                currentPage={page}
                totalPages={pagination?.totalPages || 1}
                totalItems={pagination?.totalQuestions || 0}
                itemsPerPage={pageSize}
                onPageChange={(page: number) => setPage && setPage(page)}
                showFirstLast={true}
                showPrevNext={true}
                showItemsInfo={true}
              />
            </div>
          ) : null}
        </div>
      </div>
      {openAddQuestionModal &&
        selectedQuestion &&
        createPortal(
          <AddQuestionModal
            id={selectedQuestion.cacheResponseId}
            onClose={() => {
              setOpenAddQuestionModal(false);
              setSelectedQuestion(null);
              handleCancel();
            }}
            Answer={removeCustomTags(selectedQuestion.answer, tagsToEliminate)}
            Question={selectedQuestion.query}
            type="update"
            onSave={async (updateAnswer) => {
              await handleSave(
                selectedQuestion.cacheResponseId,
                selectedQuestion.query,
                updateAnswer,
              );
              setOpenAddQuestionModal(false);
              setSelectedQuestion(null);
            }}
          />,
          document.getElementById("dropdown-portal") as HTMLElement,
        )}
      {hoveredCategoryBadge &&
        categoryDropdownPosition &&
        createPortal(
          <div
            className="fixed z-[9999] max-h-[200px] w-[188px] overflow-auto rounded-[10px] border border-gray-200 bg-white shadow-[0_0_15px_0_rgba(0,0,0,0.15)]"
            style={{
              top: `${categoryDropdownPosition.top}px`,
              left: `${categoryDropdownPosition.left}px`,
            }}
            onMouseEnter={() => setHoveredCategoryBadge(hoveredCategoryBadge)}
            onMouseLeave={() => {
              setHoveredCategoryBadge(null);
              setCategoryDropdownPosition(null);
            }}
          >
            <div className="flex flex-col gap-1 p-2">
              {(() => {
                const currentQuestion = questions.find(
                  (q) => q.cacheResponseId === hoveredCategoryBadge,
                );
                const categories = currentQuestion?.categories;

                if (!categories || !Array.isArray(categories)) {
                  return (
                    <></>
                    // <span className="flex w-auto items-center gap-[3px] whitespace-nowrap rounded-[4px] px-[12px] py-[5px] text-[12px] text-gray-500">
                    //   No categories found
                    // </span>
                  );
                }

                const maxCategoriesToShow = isSmallScreen ? 1 : 3;
                const remainingCategories =
                  categories.slice(maxCategoriesToShow);

                if (remainingCategories.length === 0) {
                  return (
                    <span className="flex w-auto items-center gap-[3px] whitespace-nowrap rounded-[4px] px-[12px] py-[5px] text-[12px] text-gray-500">
                      No additional categories
                    </span>
                  );
                }

                return remainingCategories.map((categoryName, index) => {
                  return (
                    <span
                      key={index}
                      className="flex w-auto items-center gap-[5px] whitespace-nowrap rounded-[4px] px-[8px] py-[3px] text-[12px] capitalize text-text-clr hover:bg-gray-50"
                    >
                      {categoryName}
                    </span>
                  );
                });
              })()}
            </div>
          </div>,
          document.body,
        )}
      {actionDropdownPosition &&
        Object.keys(openDropdowns).length > 0 &&
        createPortal(
          <AnimatePresence>
            {Object.entries(openDropdowns).map(
              ([cacheResponseId, isOpen]) =>
                isOpen && (
                  <motion.ul
                    key={cacheResponseId}
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="fixed z-[9999] w-[150px] cursor-pointer rounded-[10px] bg-white shadow-[0px_4px_20px_0px_rgba(0,0,0,0.10)] [&>li:first-child]:rounded-t-[10px] [&>li:last-child]:rounded-b-[10px] [&>li]:px-[16px] [&>li]:py-[12px] [&>li]:text-[14px] [&>li]:text-[#000000B2] [&>li]:transition-colors [&>li]:duration-300"
                    style={{
                      top: `${actionDropdownPosition.top}px`,
                      left: `${actionDropdownPosition.left}px`,
                    }}
                  >
                    <motion.li
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className={`flex items-center gap-[5px] text-[14px] hover:bg-primary-clr hover:text-white ${loaderState !== null ? "pointer-events-none opacity-50" : "cursor-pointer"}`}
                      onClick={() => {
                        const currentQuestion = questions.find(
                          (q) => q.cacheResponseId === cacheResponseId,
                        );
                        if (currentQuestion) handleEdit(currentQuestion);
                      }}
                    >
                      <svg
                        width="17"
                        height="17"
                        viewBox="0 0 17 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M4.92383 2.1C4.28731 2.1 3.67686 2.35286 3.22677 2.80294C2.77668 3.25303 2.52383 3.86348 2.52383 4.5V12.5C2.52383 13.1365 2.77668 13.747 3.22677 14.1971C3.67686 14.6471 4.28731 14.9 4.92383 14.9H12.9238C13.5603 14.9 14.1708 14.6471 14.6209 14.1971C15.071 13.747 15.3238 13.1365 15.3238 12.5V7.7C15.3238 7.48783 15.4081 7.28434 15.5581 7.13432C15.7082 6.98429 15.9117 6.9 16.1238 6.9C16.336 6.9 16.5395 6.98429 16.6895 7.13432C16.8395 7.28434 16.9238 7.48783 16.9238 7.7V12.5C16.9238 13.5609 16.5024 14.5783 15.7523 15.3284C15.0021 16.0786 13.9847 16.5 12.9238 16.5H4.92383C3.86296 16.5 2.84555 16.0786 2.0954 15.3284C1.34526 14.5783 0.923828 13.5609 0.923828 12.5V4.5C0.923828 3.43913 1.34526 2.42172 2.0954 1.67157C2.84555 0.921427 3.86296 0.5 4.92383 0.5H9.72383C9.936 0.5 10.1395 0.584285 10.2895 0.734315C10.4395 0.884344 10.5238 1.08783 10.5238 1.3C10.5238 1.51217 10.4395 1.71566 10.2895 1.86569C10.1395 2.01571 9.936 2.1 9.72383 2.1H4.92383Z"
                          fill="currentColor"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M13.0962 1.15601C13.5191 0.746992 14.0857 0.520537 14.674 0.52547C15.2623 0.530402 15.8251 0.766326 16.241 1.18237C16.657 1.59842 16.8927 2.16127 16.8975 2.74956C16.9023 3.33785 16.6757 3.90445 16.2666 4.32721L15.6402 4.95441C15.4902 5.10439 15.2867 5.18864 15.0746 5.18864C14.8625 5.18864 14.659 5.10439 14.509 4.95441L12.469 2.91441C12.319 2.76439 12.2348 2.56094 12.2348 2.34881C12.2348 2.13668 12.319 1.93323 12.469 1.78321L13.0962 1.15601ZM11.3378 4.04561C11.1878 3.89563 10.9843 3.81138 10.7722 3.81138C10.5601 3.81138 10.3566 3.89563 10.2066 4.04561L6.63779 7.61521C6.53535 7.71761 6.46263 7.84591 6.42739 7.98641L5.74739 10.7064C5.71398 10.8404 5.71584 10.9808 5.75278 11.1139C5.78971 11.247 5.86048 11.3682 5.95818 11.4658C6.05588 11.5635 6.1772 11.6341 6.31031 11.671C6.44342 11.7078 6.58381 11.7095 6.71779 11.676L9.43699 10.996C9.57749 10.9608 9.70579 10.8881 9.80819 10.7856L13.3778 7.21681C13.5278 7.06679 13.612 6.86334 13.612 6.65121C13.612 6.43908 13.5278 6.23563 13.3778 6.08561L11.3378 4.04561Z"
                          fill="currentColor"
                        />
                      </svg>
                      Edit Response
                    </motion.li>
                    <motion.li
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 }}
                      className={`flex items-center gap-[5px] text-[14px] hover:bg-primary-clr hover:text-white ${loaderState !== null ? "pointer-events-none opacity-50" : ""}`}
                      onClick={() => handleDelete(cacheResponseId)}
                    >
                      {selectedFilter === "expired" ? (
                        <svg
                          width="21"
                          height="21"
                          viewBox="0 0 21 21"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M15.3781 7.31799C15.2093 7.31799 15.0475 7.38503 14.9281 7.50436C14.8088 7.6237 14.7417 7.78555 14.7417 7.95432V15.0755C14.7235 15.3973 14.5788 15.6988 14.3393 15.9144C14.0997 16.13 13.7846 16.2423 13.4627 16.2266H8.3848C8.06288 16.2423 7.74782 16.13 7.50825 15.9144C7.26869 15.6988 7.12403 15.3973 7.10577 15.0755V7.95432C7.10577 7.78555 7.03873 7.6237 6.91939 7.50436C6.80006 7.38503 6.63821 7.31799 6.46944 7.31799C6.30067 7.31799 6.13882 7.38503 6.01949 7.50436C5.90015 7.6237 5.83311 7.78555 5.83311 7.95432V15.0755C5.85127 15.7349 6.13001 16.3602 6.60827 16.8145C7.08654 17.2688 7.72535 17.515 8.3848 17.4993H13.4627C14.1222 17.515 14.761 17.2688 15.2392 16.8145C15.7175 16.3602 15.9962 15.7349 16.0144 15.0755V7.95432C16.0144 7.78555 15.9474 7.6237 15.828 7.50436C15.7087 7.38503 15.5468 7.31799 15.3781 7.31799ZM16.0144 5.40899H13.4691V4.13633C13.4691 3.96757 13.402 3.80571 13.2827 3.68638C13.1634 3.56704 13.0015 3.5 12.8328 3.5H9.01477C8.846 3.5 8.68415 3.56704 8.56481 3.68638C8.44548 3.80571 8.37843 3.96757 8.37843 4.13633V5.40899H5.83311C5.66434 5.40899 5.50249 5.47604 5.38315 5.59537C5.26382 5.71471 5.19678 5.87656 5.19678 6.04533C5.19678 6.21409 5.26382 6.37594 5.38315 6.49528C5.50249 6.61461 5.66434 6.68166 5.83311 6.68166H16.0144C16.1832 6.68166 16.345 6.61461 16.4644 6.49528C16.5837 6.37594 16.6507 6.21409 16.6507 6.04533C16.6507 5.87656 16.5837 5.71471 16.4644 5.59537C16.345 5.47604 16.1832 5.40899 16.0144 5.40899ZM9.6511 5.40899V4.77266H12.1964V5.40899H9.6511Z"
                            fill="currentColor"
                          />
                          <path
                            d="M10.2873 13.6805V9.22618C10.2873 9.05741 10.2203 8.89556 10.1009 8.77622C9.9816 8.65689 9.81975 8.58984 9.65098 8.58984C9.48221 8.58984 9.32036 8.65689 9.20103 8.77622C9.08169 8.89556 9.01465 9.05741 9.01465 9.22618V13.6805C9.01465 13.8493 9.08169 14.0111 9.20103 14.1304C9.32036 14.2498 9.48221 14.3168 9.65098 14.3168C9.81975 14.3168 9.9816 14.2498 10.1009 14.1304C10.2203 14.0111 10.2873 13.8493 10.2873 13.6805ZM12.8326 13.6805V9.22618C12.8326 9.05741 12.7656 8.89556 12.6463 8.77622C12.5269 8.65689 12.3651 8.58984 12.1963 8.58984C12.0275 8.58984 11.8657 8.65689 11.7464 8.77622C11.627 8.89556 11.56 9.05741 11.56 9.22618V13.6805C11.56 13.8493 11.627 14.0111 11.7464 14.1304C11.8657 14.2498 12.0275 14.3168 12.1963 14.3168C12.3651 14.3168 12.5269 14.2498 12.6463 14.1304C12.7656 14.0111 12.8326 13.8493 12.8326 13.6805Z"
                            fill="currentColor"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M9.99963 14.9993C9.83297 14.9993 9.58297 14.916 9.49963 14.8326L6.99963 12.7493C6.6663 12.416 6.58297 11.916 6.9163 11.5826C7.24963 11.2493 7.74963 11.166 8.08297 11.4993L10.083 13.166L12.083 11.4993C12.4163 11.166 12.9996 11.2493 13.2496 11.5826C13.583 11.916 13.4996 12.4993 13.1663 12.7493L10.6663 14.8326C10.4163 14.916 10.1663 14.9993 9.99963 14.9993Z"
                            fill="currentColor"
                          />
                          <path
                            d="M10.0003 15.0007C9.50033 15.0007 9.16699 14.6673 9.16699 14.1673V9.16732C9.16699 8.66732 9.50033 8.33398 10.0003 8.33398C10.5003 8.33398 10.8337 8.66732 10.8337 9.16732V14.1673C10.8337 14.584 10.5003 15.0007 10.0003 15.0007Z"
                            fill="currentColor"
                          />
                          <path
                            d="M16.0837 18.3333H3.91699C2.66699 18.3333 1.66699 17.3333 1.66699 16.0833V5.83333C1.66699 5.33333 2.00033 5 2.50033 5H17.5003C18.0003 5 18.3337 5.33333 18.3337 5.83333V16.0833C18.3337 17.3333 17.3337 18.3333 16.0837 18.3333ZM3.33366 6.66667V16.0833C3.33366 16.4167 3.58366 16.6667 3.91699 16.6667H16.0837C16.417 16.6667 16.667 16.4167 16.667 16V6.66667H3.33366Z"
                            fill="currentColor"
                          />
                          <path
                            d="M17.5 6.66602H2.50003C2.16669 6.66602 1.91669 6.49935 1.75003 6.16602C1.58336 5.83268 1.66669 5.58268 1.83336 5.33268L4.08336 2.33268C4.41669 1.91602 4.91669 1.66602 5.41669 1.66602H14.5834C15.0834 1.66602 15.5834 1.91602 15.9167 2.33268L18.1667 5.33268C18.3334 5.58268 18.4167 5.91602 18.25 6.16602C18.0834 6.49935 17.8334 6.66602 17.5 6.66602ZM4.16669 4.99935H15.8334L14.5834 3.33268H5.41669L4.16669 4.99935Z"
                            fill="currentColor"
                          />
                        </svg>
                      )}
                      {selectedFilter === "expired"
                        ? loaderState === "delete"
                          ? "Deleting"
                          : "Delete"
                        : loaderState === "delete"
                          ? "Archiving"
                          : "Archive"}
                    </motion.li>
                    {selectedFilter == "expired" && (
                      <motion.li
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.15 }}
                        className={`flex items-center gap-[5px] text-[14px] hover:bg-primary-clr hover:text-white ${loaderState !== null ? "pointer-events-none opacity-50" : ""}`}
                        onClick={() => {
                          handleUnarchive(cacheResponseId);
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M10.1664 8.33468C10.333 8.33468 10.583 8.41801 10.6664 8.50134L13.1664 10.5847C13.4997 10.918 13.583 11.418 13.2497 11.7513C12.9164 12.0847 12.4164 12.168 12.083 11.8347L10.083 10.168L8.08305 11.8347C7.74971 12.168 7.16638 12.0847 6.91638 11.7513C6.58305 11.418 6.66638 10.8347 6.99971 10.5847L9.49971 8.50134C9.74971 8.41801 9.99971 8.33468 10.1664 8.33468Z"
                            fill="currentColor"
                          />
                          <path
                            d="M10.1657 8.33333C10.6657 8.33333 10.999 8.66667 10.999 9.16667V14.1667C10.999 14.6667 10.6657 15 10.1657 15C9.66569 15 9.33236 14.6667 9.33236 14.1667V9.16667C9.33236 8.75 9.66569 8.33333 10.1657 8.33333Z"
                            fill="currentColor"
                          />
                          <path
                            d="M16.0837 18.3333H3.91699C2.66699 18.3333 1.66699 17.3333 1.66699 16.0833V5.83333C1.66699 5.33333 2.00033 5 2.50033 5H17.5003C18.0003 5 18.3337 5.33333 18.3337 5.83333V16.0833C18.3337 17.3333 17.3337 18.3333 16.0837 18.3333ZM3.33366 6.66667V16.0833C3.33366 16.4167 3.58366 16.6667 3.91699 16.6667H16.0837C16.417 16.6667 16.667 16.4167 16.667 16V6.66667H3.33366Z"
                            fill="currentColor"
                          />
                          <path
                            d="M17.5 6.66602H2.50003C2.16669 6.66602 1.91669 6.49935 1.75003 6.16602C1.58336 5.83268 1.66669 5.58268 1.83336 5.33268L4.08336 2.33268C4.41669 1.91602 4.91669 1.66602 5.41669 1.66602H14.5834C15.0834 1.66602 15.5834 1.91602 15.9167 2.33268L18.1667 5.33268C18.3334 5.58268 18.4167 5.91602 18.25 6.16602C18.0834 6.49935 17.8334 6.66602 17.5 6.66602ZM4.16669 4.99935H15.8334L14.5834 3.33268H5.41669L4.16669 4.99935Z"
                            fill="currentColor"
                          />
                        </svg>
                        {loaderState === "unarchive"
                          ? "Unarchiving..."
                          : "Unarchive"}
                      </motion.li>
                    )}
                  </motion.ul>
                ),
            )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  );
}

export default MostAskedQuestions;

const IncreasedSortIcon = () => (
  <svg
    width={20}
    height={20}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width={20} height={20} rx={4.44444} fill="#FFE9E9" />
    <path
      d="M12.9474 15.895H2.84211C2.33684 15.895 2 15.5581 2 15.0529C2 14.5476 2.33684 14.2107 2.84211 14.2107H12.9474C13.4526 14.2107 13.7895 14.5476 13.7895 15.0529C13.7895 15.5581 13.4526 15.895 12.9474 15.895ZM10.4211 11.6844H2.84211C2.33684 11.6844 2 11.3476 2 10.8423C2 10.3371 2.33684 10.0002 2.84211 10.0002H10.4211C10.9263 10.0002 11.2632 10.3371 11.2632 10.8423C11.2632 11.3476 10.9263 11.6844 10.4211 11.6844ZM7.89475 7.4739H2.84211C2.33684 7.4739 2 7.13705 2 6.63179C2 6.12652 2.33684 5.78968 2.84211 5.78968H7.89475C8.40001 5.78968 8.73685 6.12652 8.73685 6.63179C8.73685 7.13705 8.40001 7.4739 7.89475 7.4739ZM14.6316 12.5265C14.1263 12.5265 13.7895 12.1897 13.7895 11.6844V4.94758C13.7895 4.44231 14.1263 4.10547 14.6316 4.10547C15.1369 4.10547 15.4737 4.44231 15.4737 4.94758V11.6844C15.4737 12.1897 15.1369 12.5265 14.6316 12.5265Z"
      fill="#FB5F66"
    />
    <path
      d="M17.1579 8.316C16.9053 8.316 16.7369 8.23179 16.5685 8.06337L14.6316 6.12652L12.6948 8.06337C12.3579 8.40021 11.8527 8.40021 11.5158 8.06337C11.179 7.72653 11.179 7.22126 11.5158 6.88442L14.0421 4.3581C14.379 4.02126 14.8842 4.02126 15.2211 4.3581L17.7474 6.88442C18.0842 7.22126 18.0842 7.72653 17.7474 8.06337C17.579 8.23179 17.4106 8.316 17.1579 8.316Z"
      fill="#FB5F66"
    />
  </svg>
);

const DecreasedSortIcon = () => (
  <svg
    width={20}
    height={20}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width={20} height={20} rx={4.44444} fill="#FFE9E9" />
    <path
      d="M7.89425 14.2095H2.84162C2.33635 14.2095 1.99951 13.8727 1.99951 13.3674C1.99951 12.8621 2.33635 12.5253 2.84162 12.5253H7.89425C8.39951 12.5253 8.73635 12.8621 8.73635 13.3674C8.73635 13.8727 8.39951 14.2095 7.89425 14.2095ZM10.4206 9.99897H2.84162C2.33635 9.99897 1.99951 9.66213 1.99951 9.15687C1.99951 8.6516 2.33635 8.31476 2.84162 8.31476H10.4206C10.9258 8.31476 11.2627 8.6516 11.2627 9.15687C11.2627 9.66213 10.9258 9.99897 10.4206 9.99897ZM14.6311 15.8937C14.1258 15.8937 13.789 15.5569 13.789 15.0516V8.31476C13.789 7.8095 14.1258 7.47266 14.6311 7.47266C15.1364 7.47266 15.4732 7.8095 15.4732 8.31476V15.0516C15.4732 15.5569 15.1364 15.8937 14.6311 15.8937Z"
      fill="#FB5F66"
    />
    <path
      d="M14.6311 15.8949C14.3785 15.8949 14.21 15.8107 14.0416 15.6423L11.5153 13.116C11.1785 12.7792 11.1785 12.2739 11.5153 11.937C11.8521 11.6002 12.3574 11.6002 12.6942 11.937L14.6311 13.8739L16.5679 11.937C16.9048 11.6002 17.41 11.6002 17.7469 11.937C18.0837 12.2739 18.0837 12.7792 17.7469 13.116L15.2206 15.6423C15.0521 15.8107 14.8837 15.8949 14.6311 15.8949ZM12.9469 5.78968H2.84162C2.33635 5.78968 1.99951 5.45284 1.99951 4.94757C1.99951 4.44231 2.33635 4.10547 2.84162 4.10547H12.9469C13.4521 4.10547 13.789 4.44231 13.789 4.94757C13.789 5.45284 13.4521 5.78968 12.9469 5.78968Z"
      fill="#FB5F66"
    />
  </svg>
);
